import React, { useState, useEffect, useMemo } from 'react';
import { Row, Col, Badge, Container } from 'react-bootstrap';
import { 
  IconMessage, 
  IconTrendingUp, 
  IconTrendingDown,
  IconBook,
  IconBox,
  IconChartBar,
  IconDeviceAnalytics,
  IconBrain,
  IconCalendar,
  IconRobot,
  IconPhone,
  IconWifi,
  IconWifiOff
} from '@tabler/icons-react';
import { useAgentHubContext } from '../contexts/AgentHubContext';

// Skeleton component
const MetricCardSkeleton = () => (
  <div className="metric-card skeleton slide-up">
    <div className="metric-header">
      <div className="metric-icon skeleton-shimmer"></div>
      <div className="metric-trend skeleton-shimmer skeleton-trend"></div>
    </div>
    <div className="metric-value skeleton-shimmer skeleton-value"></div>
    <div className="metric-label skeleton-shimmer skeleton-label"></div>
    <div className="metric-description skeleton-shimmer skeleton-description"></div>
  </div>
);

const Dashboard = ({ hubMode = false, agentId = null, agentContext = null }) => {
  const { agent } = useAgentHubContext();
  const [loading, setLoading] = useState(true);

  // Simulação de carregamento
  useEffect(() => {
    if (agent) {
      const timer = setTimeout(() => setLoading(false), 800);
      return () => clearTimeout(timer);
    }
  }, [agent]);

  // Helper functions
  const getStatusBadge = () => {
    if (!agent) return null;
    if (agent.is_active) {
      return (
        <Badge bg="success" className="status-badge d-flex align-items-center">
          <IconWifi size={12} className="me-1" />
          Ativo
        </Badge>
      );
    }
    return (
      <Badge bg="secondary" className="status-badge d-flex align-items-center">
        <IconWifiOff size={12} className="me-1" />
        Inativo
      </Badge>
    );
  };

  const getConnectionStatus = () => {
    if (!agent) return null;
    if (agent.wabotty_account) {
      return (
        <div className="d-flex align-items-center text-success">
          <IconPhone size={16} className="me-2" />
          <span className="fw-medium">WhatsApp:</span>
          <span className="ms-1">{agent.wabotty_account}</span>
        </div>
      );
    }
    return (
      <div className="d-flex align-items-center text-warning">
        <IconPhone size={16} className="me-2" />
        <span className="fw-medium">WhatsApp:</span>
        <span className="ms-1">não conectado</span>
      </div>
    );
  };

  const formatCreatedDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit', 
      year: 'numeric'
    });
  };

  // Calcular métricas principais baseadas nos dados reais do agente
  const primaryMetrics = useMemo(() => {
    if (!agent) return [];

    const daysActive = Math.floor((new Date() - new Date(agent.created_at)) / (1000 * 60 * 60 * 24));
    const avgMessagesPerDay = agent.total_sent_messages / Math.max(daysActive, 1);
    
    // Estimativas baseadas nos dados reais
    const todayMessages = Math.floor(avgMessagesPerDay * 0.8);
    
    return [
      {
        id: "total_messages",
        icon: IconMessage,
        value: agent.total_sent_messages.toLocaleString(),
        label: "Total de Mensagens",
        description: `${todayMessages} mensagens hoje`,
        trend: todayMessages > 10 ? "positive" : "neutral",
        growth: todayMessages > 10 ? `+${((todayMessages / avgMessagesPerDay - 1) * 100).toFixed(1)}%` : "Baixa atividade",
        priority: 1,
        color: "linear-gradient(135deg, #137ff7, #1ea7c5)",
      },
      {
        id: "products",
        icon: IconBox,
        value: agent.products_count,
        label: "Produtos",
        description: "Catálogo disponível",
        trend: agent.products_count > 20 ? "positive" : agent.products_count > 10 ? "neutral" : "negative",
        growth: agent.products_count > 20 ? "Rico" : agent.products_count > 10 ? "Médio" : "Limitado",
        priority: 2,
        color: "linear-gradient(135deg, #1ea7c5, #137ff7)",
      },
      {
        id: "knowledge",
        icon: IconBook,
        value: agent.documents_count,
        label: "Base de Conhecimento",
        description: "Documentos treinados",
        trend: agent.documents_count > 10 ? "positive" : agent.documents_count > 5 ? "neutral" : "negative",
        growth: agent.documents_count > 10 ? "Completa" : agent.documents_count > 5 ? "Básica" : "Inicial",
        priority: 3,
        color: "linear-gradient(135deg, #6f42c1, #e83e8c)",
      },
    ];
  }, [agent]);

  // Renderização do loading
  if (loading && agent) {
    return (
      <div className="dashboard">
        <div className="dashboard-grid grid-3">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <MetricCardSkeleton key={i} />
          ))}
        </div>
      </div>
    );
  }

  if (!agent) {
    return (
      <div className="dashboard">
        <div className="text-center py-5">
          <IconDeviceAnalytics size={64} className="text-muted mb-3" />
          <h5 className="text-muted">Carregando dados do agente...</h5>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard">
      {/* Header integrado ao Dashboard */}
      <div className="modern-agent-header">
          <Row className="align-items-center">
            <Col md={12}>
              <div className="agent-header-card">
                <div className="d-flex align-items-center gap-4">
                  <div className="modern-agent-avatar">
                    {agent.agent_image ? (
                      <img 
                        src={agent.agent_image} 
                        alt={agent.agent_name}
                        className="avatar-image"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.nextSibling.style.display = 'flex';
                        }}
                      />
                    ) : null}
                    <div className="avatar-fallback" style={{ display: agent.agent_image ? 'none' : 'flex' }}>
                      <IconRobot size={28} />
                    </div>
                    {agent.is_active && (
                      <div className="modern-status-indicator active"></div>
                    )}
                  </div>
                  <div className="flex-grow-1">
                    <div className="d-flex align-items-center flex-wrap gap-2 mb-2">
                      <h1 className="agent-name mb-0">{agent.agent_name}</h1>
                      {getStatusBadge()}
                      <Badge bg="light" text="dark" className="version-badge">
                        v{agent.version_active}
                      </Badge>
                    </div>
                    <div className="agent-meta">
                      <span className="agent-role">
                        {agent.agent_job || 'Assistente IA'} • {agent.agent_area}
                      </span>
                      <span className="separator">•</span>
                      <span className="created-date">
                        Criado em {formatCreatedDate(agent.created_at)}
                      </span>
                    </div>
                    <div className="connection-status mt-2">
                      {getConnectionStatus()}
                    </div>
                  </div>
                </div>
              </div>
            </Col>
          </Row>
      </div>

      {/* Métricas Principais */}
      <section className="metrics-section">
        <div className="section-header">
          <h4 className="section-title">
            <IconChartBar size={20} />
            Performance do Agente
          </h4>
          <div className="section-info">
            <span className="badge bg-light text-dark">
              <IconBrain size={14} className="me-1" />
              {agent.model}
            </span>
            <span className="badge bg-light text-dark ms-2">
              <IconCalendar size={14} className="me-1" />
              v{agent.version_active}
            </span>
          </div>
        </div>

        <div className="dashboard-grid grid-3">
          {primaryMetrics.map((metric, index) => {
            const IconComponent = metric.icon;
            return (
              <div
                key={metric.id}
                className="metric-card fade-in"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <div className="metric-header">
                  <div
                    className="metric-icon"
                    style={metric.color ? { background: metric.color } : {}}
                  >
                    <IconComponent size={20} />
                  </div>
                 
                </div>
                <div className="metric-value">{metric.value}</div>
                <h3 className="metric-label">{metric.label}</h3>
                <p className="metric-description">{metric.description}</p>
              </div>
            );
          })}
        </div>
      </section>

    </div>
  );
};

export default Dashboard;